import { useState, useMemo, useCallback } from 'react';
import { Box, CircularProgress, Typography, IconButton, Tooltip } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
    getBackofficeDocumentUploads,
    getBackofficeDocumentUploadsByAgent,
} from 'api/rest/analytics';
import { formatLocalizedShortDate } from 'utils/formatDate';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import type {
    BackofficeDocumentUploadsSummaryData,
    BackofficeDocumentUploadsDrilldownData,
    BackofficeUploadDetail,
} from 'hooks/analytics';
import {
    AnalyticsColors,
    SectionCard,
    SectionTitle,
    stateMessageSx,
    loadingWrapSx,
    thSx,
    tdSx,
    trHoverSx,
} from '../../components/analyticsUi';

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------
const kpiCardSx = {
    flex: '1 1 200px',
    background: AnalyticsColors.primaryLight,
    borderRadius: '10px',
    p: '1.25rem 1.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
};

const KpiCard = ({ label, value }: { label: string; value: number }) => (
    <Box sx={kpiCardSx}>
        <Typography
            sx={{
                fontSize: 12,
                fontWeight: 600,
                color: AnalyticsColors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: 28,
                fontWeight: 700,
                color: AnalyticsColors.primary,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {value.toLocaleString()}
        </Typography>
    </Box>
);

// ---------------------------------------------------------------------------
// Sortable column helpers
// ---------------------------------------------------------------------------
type SortKey = 'agentName' | 'agentEmail' | 'uploadsCount';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({ direction }: { direction: SortDirection }) => (
    <Box
        component="span"
        sx={{ fontSize: 10, lineHeight: 1, color: AnalyticsColors.primary, ml: '4px' }}
    >
        {direction === 'asc' ? '▲' : '▼'}
    </Box>
);

// ---------------------------------------------------------------------------
// Agent Summary Table (Endpoint 1)
// ---------------------------------------------------------------------------
interface AgentTableProps {
    data: BackofficeDocumentUploadsSummaryData;
    onAgentClick: (agentId: number, agentName: string) => void;
}

const AgentSummaryTable = ({ data, onAgentClick }: AgentTableProps) => {
    const [sortKey, setSortKey] = useState<SortKey>('uploadsCount');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');

    const handleSort = (key: SortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir(key === 'uploadsCount' ? 'desc' : 'asc');
        }
    };

    const sorted = useMemo(() => {
        const copy = [...data.agents];
        copy.sort((a, b) => {
            let diff: number;
            if (sortKey === 'uploadsCount') {
                diff = a.uploadsCount - b.uploadsCount;
            } else {
                diff = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '');
            }
            return sortDir === 'desc' ? -diff : diff;
        });
        return copy;
    }, [data.agents, sortKey, sortDir]);

    const renderTh = (label: string, key: SortKey, align?: 'left' | 'right') => (
        <Box
            component="th"
            sx={thSx({ align: align ?? 'left', sortable: true, active: sortKey === key })}
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer' }}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    width: '100%',
                }}
            >
                {label}
                {sortKey === key && <SortIndicator direction={sortDir} />}
            </Box>
        </Box>
    );

    return (
        <>
            {/* KPI cards */}
            <Box sx={{ display: 'flex', gap: '1rem', mb: '1.5rem', flexWrap: 'wrap' }}>
                <KpiCard label="Total Uploads" value={data.summary.totalUploads} />
                <KpiCard label="Agents" value={data.summary.agentCount} />
            </Box>

            {sorted.length === 0 ? (
                <Box sx={stateMessageSx}>No uploads for this period.</Box>
            ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {renderTh('Agent Name', 'agentName')}
                            {renderTh('Email', 'agentEmail')}
                            {renderTh('Uploads', 'uploadsCount', 'right')}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((agent) => (
                            <Box
                                component="tr"
                                key={agent.agentId}
                                sx={{
                                    ...trHoverSx,
                                    cursor: 'pointer',
                                    '&:hover': { background: AnalyticsColors.primaryLight },
                                }}
                                onClick={() => onAgentClick(agent.agentId, agent.agentName)}
                            >
                                <Box component="td" sx={tdSx()}>
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 600, color: AnalyticsColors.primary }}
                                    >
                                        {agent.agentName}
                                    </Box>
                                </Box>
                                <Box component="td" sx={tdSx()}>
                                    {agent.agentEmail}
                                </Box>
                                <Box component="td" sx={tdSx({ align: 'right', bold: true })}>
                                    {agent.uploadsCount.toLocaleString()}
                                </Box>
                            </Box>
                        ))}
                    </tbody>
                </Box>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Agent Drilldown Table (Endpoint 2)
// ---------------------------------------------------------------------------
type DrilldownSortKey =
    | 'documentName'
    | 'clientName'
    | 'caseId'
    | 'programName'
    | 'backofficeName'
    | 'salesName'
    | 'consultantName'
    | 'uploadedAt';

interface DrilldownTableProps {
    data: BackofficeDocumentUploadsDrilldownData;
    onBack: () => void;
}

const AgentDrilldownTable = ({ data, onBack }: DrilldownTableProps) => {
    const [sortKey, setSortKey] = useState<DrilldownSortKey>('uploadedAt');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');

    const handleSort = (key: DrilldownSortKey) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortKey(key);
            setSortDir(key === 'uploadedAt' || key === 'caseId' ? 'desc' : 'asc');
        }
    };

    const sorted = useMemo(() => {
        const copy = [...data.uploads];
        copy.sort((a, b) => {
            let diff: number;
            switch (sortKey) {
                case 'caseId':
                    diff = (a.caseId ?? 0) - (b.caseId ?? 0);
                    break;
                case 'uploadedAt':
                    diff = (a.uploadedAt ?? '').localeCompare(b.uploadedAt ?? '');
                    break;
                default:
                    diff = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '');
            }
            return sortDir === 'desc' ? -diff : diff;
        });
        return copy;
    }, [data.uploads, sortKey, sortDir]);

    const renderTh = (label: string, key: DrilldownSortKey, align?: 'left' | 'right') => (
        <Box
            component="th"
            sx={thSx({ align: align ?? 'left', sortable: true, active: sortKey === key })}
            onClick={() => handleSort(key)}
            style={{ cursor: 'pointer' }}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    width: '100%',
                }}
            >
                {label}
                {sortKey === key && <SortIndicator direction={sortDir} />}
            </Box>
        </Box>
    );

    return (
        <>
            {/* Back button + agent info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', mb: '1.25rem' }}>
                <Tooltip title="Back to all agents">
                    <IconButton
                        onClick={onBack}
                        size="small"
                        sx={{ color: AnalyticsColors.primary }}
                    >
                        <ArrowBack fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box>
                    <Typography
                        sx={{ fontSize: 16, fontWeight: 700, color: AnalyticsColors.textPrimary }}
                    >
                        {data.agent?.agentName ?? 'Unknown Agent'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: AnalyticsColors.textSecondary }}>
                        {data.agent?.agentEmail ?? '—'} · {data.totalUploads.toLocaleString()}{' '}
                        upload{data.totalUploads !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            </Box>

            {sorted.length === 0 ? (
                <Box sx={stateMessageSx}>No uploads found for this agent.</Box>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        component="table"
                        sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}
                    >
                        <thead>
                            <tr>
                                {renderTh('Document Name', 'documentName')}
                                {renderTh('Client Name', 'clientName')}
                                {renderTh('Program', 'programName')}
                                {renderTh('Back Office', 'backofficeName')}
                                {renderTh('Sales', 'salesName')}
                                {renderTh('Consultant', 'consultantName')}
                                {renderTh('Uploaded At', 'uploadedAt')}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((upload: BackofficeUploadDetail) => (
                                <Box component="tr" key={upload.documentId} sx={trHoverSx}>
                                    <Box component="td" sx={tdSx()}>
                                        {upload.documentName ?? '—'}
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {upload.clientName ?? '—'}
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {upload.programName ?? '—'}
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {upload.backofficeName ?? '—'}
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {upload.salesName ?? '—'}
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {upload.consultantName ?? '—'}
                                    </Box>
                                    <Box component="td" sx={tdSx()}>
                                        {formatLocalizedShortDate(upload.uploadedAt, {
                                            includeTime: true,
                                        })}
                                    </Box>
                                </Box>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------
interface BackofficeUploadsViewProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const BackofficeUploadsView = ({ from, to, serviceProviderId }: BackofficeUploadsViewProps) => {
    // Selected agent for drilldown (null = show summary)
    const [selectedAgent, setSelectedAgent] = useState<{ id: number; name: string } | null>(null);

    // Endpoint 1 — Summary
    const summary = useAnalyticsData<BackofficeDocumentUploadsSummaryData>(
        () => getBackofficeDocumentUploads(from, to, serviceProviderId),
        [from, to, serviceProviderId]
    );

    // Endpoint 2 — Drilldown (only fetched when an agent is selected)
    const drilldown = useAnalyticsData<BackofficeDocumentUploadsDrilldownData>(
        () =>
            selectedAgent
                ? getBackofficeDocumentUploadsByAgent(selectedAgent.id, from, to, serviceProviderId)
                : Promise.reject('no agent'),
        [selectedAgent?.id, from, to, serviceProviderId]
    );

    const handleAgentClick = useCallback((agentId: number, agentName: string) => {
        setSelectedAgent({ id: agentId, name: agentName });
    }, []);

    const handleBack = useCallback(() => {
        setSelectedAgent(null);
    }, []);

    // Determine which view to show
    const showDrilldown = selectedAgent !== null;

    return (
        <SectionCard>
            <SectionTitle>Document Uploads</SectionTitle>

            {showDrilldown ? (
                // Drilldown view
                drilldown.loading ? (
                    <Box sx={loadingWrapSx}>
                        <CircularProgress size={28} />
                    </Box>
                ) : drilldown.error ? (
                    <Box sx={stateMessageSx}>{drilldown.error}</Box>
                ) : drilldown.data ? (
                    <AgentDrilldownTable data={drilldown.data} onBack={handleBack} />
                ) : null
            ) : // Summary view
            summary.loading ? (
                <Box sx={loadingWrapSx}>
                    <CircularProgress size={28} />
                </Box>
            ) : summary.error ? (
                <Box sx={stateMessageSx}>{summary.error}</Box>
            ) : summary.data ? (
                <AgentSummaryTable data={summary.data} onAgentClick={handleAgentClick} />
            ) : null}
        </SectionCard>
    );
};

export default BackofficeUploadsView;
